/**
 * Clock layout guardrails.
 * Keep these values stable unless intentionally changing the clock lane UX.
 */

export const CLOCK_SAFE_TOP_OFFSET_PX = 2;
export const CLOCK_LAYER_Z_INDEX = 1301;
/** Voice Nav M3 dest dock + search composer (`CLOCK_LAYER + 3`). */
export const VOICE_NAV_M3_TOOLBAR_Z_INDEX = CLOCK_LAYER_Z_INDEX + 3;
/**
 * MUI Dialog/Modal must sit above the M3 dest dock, search composer, FAB rails,
 * landscape fab grid (1600), page frost (1800), and map-fab popouts (~2000).
 * Location Access and every other Dialog inherit this via theme.zIndex.modal.
 */
export const APP_MODAL_Z_INDEX = 2200;
/** Above the Voice Nav “Tap API / search” composer (CLOCK_LAYER + 3). */
export const ARCHIVE_FAB_DRAWER_Z_INDEX = CLOCK_LAYER_Z_INDEX + 20;
// Room for weather-style price tiles (benchmarks + products + oilfield stock strip + info row).
export const HOST_HEADER_COMMODITY_HEIGHT_PX = 196;

/**
 * Clearance past safe-area so center tiles (stocks / weather) never cover the
 * voice-nav left desktop rail (~72–88px) or right FAB rail (~88–96px).
 * Keep weather overlay + commodity host strip on the same inset language.
 */
export const VOICE_NAV_CONTENT_SIDE_INSET_LEFT_CSS = {
  xs: 'calc(env(safe-area-inset-left, 0px) + 96px)',
  sm: 'calc(env(safe-area-inset-left, 0px) + 104px)',
  md: 'calc(env(safe-area-inset-left, 0px) + 112px)',
  lg: 'calc(env(safe-area-inset-left, 0px) + 120px)',
  xl: 'calc(env(safe-area-inset-left, 0px) + 128px)',
} as const;

export const VOICE_NAV_CONTENT_SIDE_INSET_RIGHT_CSS = {
  xs: 'calc(env(safe-area-inset-right, 0px) + 96px)',
  sm: 'calc(env(safe-area-inset-right, 0px) + 104px)',
  md: 'calc(env(safe-area-inset-right, 0px) + 112px)',
  lg: 'calc(env(safe-area-inset-right, 0px) + 120px)',
  xl: 'calc(env(safe-area-inset-right, 0px) + 128px)',
} as const;

/** When taskbars are hidden, tiles can use a tight gutter only. */
export const VOICE_NAV_CONTENT_SIDE_INSET_HIDDEN_BARS_CSS = {
  left: 'calc(env(safe-area-inset-left, 0px) + 10px)',
  right: 'calc(env(safe-area-inset-right, 0px) + 10px)',
} as const;

export const VOICE_COMMODITY_TOP_OFFSETS_PX = {
  xs: 46,
  sm: 50,
  md: 54,
  lg: 64,
} as const;

export const VOICE_COMMODITY_MIN_TOP_PX = {
  xs: 102,
  sm: 106,
  md: 112,
  lg: 122,
} as const;
export const VOICE_MARKET_STRIP_BOTTOM_OFFSET_PX = 14;

// Favorites map top-row controls sit below the clock lane on phone screens.
export const FAVORITES_MOBILE_CLOCK_CLEARANCE_PX = 30;

// Voice screen top-right favorites FAB offsets by breakpoint.
export const VOICE_TOP_ACTION_OFFSETS_PX = {
  xs: 46,
  sm: 50,
  md: 55,
  lg: 65,
} as const;

export const IOS_VOICE_HEADER_UTILITY_TOP_OFFSETS_PX = {
  xs: 6,
  sm: 8,
  md: 10,
  lg: 12,
} as const;

export const IOS_VOICE_HEADER_UTILITY_MIN_TOP_PX = {
  xs: 8,
  sm: 8,
  md: 10,
  lg: 12,
} as const;

export const CLOCK_GUARDRAIL_VIEWPORTS = {
  iphone: { width: 390, height: 844 },
  androidSmall: { width: 360, height: 800 },
  desktop: { width: 1366, height: 768 },
} as const;

export function buildSafeTopOffsetCss(offsetPx: number): string {
  return `calc(var(--tg-content-safe-top, 0px) + ${offsetPx}px)`;
}

export function buildClockTopCss(): string {
  return buildSafeTopOffsetCss(CLOCK_SAFE_TOP_OFFSET_PX);
}

export function buildHostHeaderCommodityTopCss(): string {
  return `max(2px, calc(var(--tg-content-safe-top, 0px) - ${HOST_HEADER_COMMODITY_HEIGHT_PX + 2}px))`;
}

export function buildHostHeaderClockTopCss(): string {
  return buildHostHeaderCommodityTopCss();
}

export function buildVoiceCommodityTickerTopCss() {
  return {
    xs: `max(${VOICE_COMMODITY_MIN_TOP_PX.xs}px, ${buildSafeTopOffsetCss(VOICE_COMMODITY_TOP_OFFSETS_PX.xs)})`,
    sm: `max(${VOICE_COMMODITY_MIN_TOP_PX.sm}px, ${buildSafeTopOffsetCss(VOICE_COMMODITY_TOP_OFFSETS_PX.sm)})`,
    md: `max(${VOICE_COMMODITY_MIN_TOP_PX.md}px, ${buildSafeTopOffsetCss(VOICE_COMMODITY_TOP_OFFSETS_PX.md)})`,
    lg: `max(${VOICE_COMMODITY_MIN_TOP_PX.lg}px, ${buildSafeTopOffsetCss(VOICE_COMMODITY_TOP_OFFSETS_PX.lg)})`,
  };
}

export function buildIosVoiceHeaderUtilityTopCss() {
  return {
    xs: `max(${IOS_VOICE_HEADER_UTILITY_MIN_TOP_PX.xs}px, ${buildSafeTopOffsetCss(IOS_VOICE_HEADER_UTILITY_TOP_OFFSETS_PX.xs)})`,
    sm: `max(${IOS_VOICE_HEADER_UTILITY_MIN_TOP_PX.sm}px, ${buildSafeTopOffsetCss(IOS_VOICE_HEADER_UTILITY_TOP_OFFSETS_PX.sm)})`,
    md: `max(${IOS_VOICE_HEADER_UTILITY_MIN_TOP_PX.md}px, ${buildSafeTopOffsetCss(IOS_VOICE_HEADER_UTILITY_TOP_OFFSETS_PX.md)})`,
    lg: `max(${IOS_VOICE_HEADER_UTILITY_MIN_TOP_PX.lg}px, ${buildSafeTopOffsetCss(IOS_VOICE_HEADER_UTILITY_TOP_OFFSETS_PX.lg)})`,
  };
}

export function buildVoiceMarketStripBottomCss(): string {
  return `calc(env(safe-area-inset-bottom, 0px) + ${VOICE_MARKET_STRIP_BOTTOM_OFFSET_PX}px)`;
}

export function isDesktopViewport(widthPx: number): boolean {
  return widthPx >= 768;
}

export function getFavoritesMobileClockClearancePx(isDesktop: boolean): number {
  return isDesktop ? 0 : FAVORITES_MOBILE_CLOCK_CLEARANCE_PX;
}
